import {
  Component,
  ElementRef,
  QueryList,
  ViewChildren,
  effect,
  inject,
} from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import {
  ToastMessageService,
  ToastType,
  ToastMessageInstance,
} from '../../services/toast-message.service';

@Component({
  selector: 'app-toast-message',
  standalone: true,
  imports: [MatIcon],
  templateUrl: './toast-message.component.html',
  styleUrls: ['./toast-message.component.scss'],
})
export class ToastMessageComponent {
  private readonly toastService = inject(ToastMessageService);
  @ViewChildren('toastRef') private toastRefs?: QueryList<ElementRef<HTMLElement>>;
  private readonly positions = new Map<number, number>();
  private pendingAnimationIds: number[] = [];
  private animationScheduled = false;

  readonly toasts = this.toastService.toasts;
  readonly ToastType = ToastType;

  constructor() {
    effect(() => {
      const current = this.toasts();
      this.scheduleStackAnimation(current.map((item) => item.id));
    });
  }

  protected toastRole(type: ToastType): 'alert' | 'status' {
    return type === ToastType.Error || type === ToastType.Warning ? 'alert' : 'status';
  }

  protected toastAriaLive(type: ToastType): 'assertive' | 'polite' {
    return type === ToastType.Error || type === ToastType.Warning ? 'assertive' : 'polite';
  }

  protected dismiss(toast: ToastMessageInstance): void {
    if (!toast.dismissible) return;
    this.toastService.dismiss(toast.id);
  }

  private scheduleStackAnimation(ids: number[]): void {
    this.pendingAnimationIds = ids;
    if (this.animationScheduled) return;
    this.animationScheduled = true;
    requestAnimationFrame(() => {
      this.animationScheduled = false;
      this.runStackAnimation(this.pendingAnimationIds);
    });
  }

  private runStackAnimation(ids: number[]): void {
    if (!this.toastRefs) return;
    const references = this.toastRefs.toArray();
    if (!references.length) {
      this.positions.clear();
      return;
    }

    const nextPositions = new Map<number, number>();
    const idLookup = new Set(ids);

    references.forEach((ref) => {
      const element = ref.nativeElement;
      const identifier = Number(element.getAttribute('data-toast-id'));
      if (!idLookup.has(identifier)) return;

      const currentTop = element.offsetTop;
      nextPositions.set(identifier, currentTop);

      const previousTop = this.positions.get(identifier);
      if (previousTop === undefined) {
        element.style.setProperty('--toast-y', '0px');
        return;
      }

      const delta = previousTop - currentTop;
      if (!delta) {
        element.style.setProperty('--toast-y', '0px');
        return;
      }

      element.style.transition = 'none';
      element.style.setProperty('--toast-y', `${delta}px`);
      void element.offsetHeight;
      element.style.transition = '';
      element.style.setProperty('--toast-y', '0px');
    });

    this.positions.clear();
    nextPositions.forEach((value, key) => this.positions.set(key, value));
  }
}
